import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Comment } from '../types/comment';
import { ResourceType } from '@ml/shared/types/resource-type';
import { filter, tap } from 'rxjs/operators';
import { CrudApiService } from '@ml/shared/services/crud-api.service';
import { QueryParams } from '@ml/shared/types/query-params';

@Injectable({
  providedIn: 'root'
})
export class CommentsService extends CrudApiService<Comment> {
  private resourceId$ = new BehaviorSubject<number>(null);
  private resource$ = new BehaviorSubject<ResourceType>(null);
  private title$ = new BehaviorSubject<string>(null);

  private commentAdded = new BehaviorSubject<{
    resourceId: number;
    resourceType: ResourceType;
  }>(null);
  private commentRemoved = new BehaviorSubject<{
    resourceId: number;
    resourceType: ResourceType;
  }>(null);

  constructor(private http: HttpClient) {
    super(http, true);
  }

  get url() {
    return `${this.resource$.value}/${this.resourceId$.value}/comments`;
  }

  get title(): Observable<string> {
    return this.title$.asObservable();
  }

  subscribeValues$(): Observable<Comment[]> {
    return super.subscribeValues$();
  }

  setDetails(resource: ResourceType, resourceId: number, title?: string) {
    this.resource$.next(resource);
    this.resourceId$.next(resourceId);
    this.title$.next(title);
  }

  add(comment: Comment): Observable<Comment> {
    return super.add(comment).pipe(tap(() => this.onAddComment()));
  }

  delete(id: number): Observable<any> {
    return super.delete(id).pipe(tap(() => this.onRemoveComment()));
  }

  protected deleteUrl(id: number): string {
    return `comments/${id}`;
  }

  protected getInitialQueryParams() {
    const queryParams = new QueryParams();
    queryParams.pageSize = 15;
    return queryParams;
  }

  unsubscribeManageComments() {
    this.commentAdded = new BehaviorSubject<{
      resourceId: number;
      resourceType: ResourceType;
    }>(null);
    this.commentRemoved = new BehaviorSubject<{
      resourceId: number;
      resourceType: ResourceType;
    }>(null);
  }

  subscribeCommentAdded(resourceId: number, resourceType: ResourceType) {
    return this.commentAdded
      .asObservable()
      .pipe(
        filter(
          value =>
            value &&
            value.resourceId === resourceId &&
            value.resourceType === resourceType
        )
      );
  }

  subscribeCommentRemoved(resourceId: number, resourceType: ResourceType) {
    return this.commentRemoved
      .asObservable()
      .pipe(
        filter(
          value =>
            value &&
            value.resourceId === resourceId &&
            value.resourceType === resourceType
        )
      );
  }

  private onAddComment() {
    if (this.values.value.length === 1) {
      this.commentAdded.next(this.resources);
    }
  }

  private onRemoveComment() {
    if (this.values.value.length === 0) {
      this.commentRemoved.next(this.resources);
    }
  }

  private get resources() {
    return {
      resourceId: this.resourceId$.value,
      resourceType: this.resource$.value
    };
  }
}
